import {
  compareTankKinds,
  compareTankNames,
  fromCanonical,
  LAYOUT_WIDTHS,
  layoutDepth,
  toFixedTrimmed,
  USUAL_LAYOUT_WIDTH,
} from "@bl/domain";
import type { Tank, TankStay } from "./api";

/**
 * The Brewery layout (Tyler, 2026-10-04): the cellar's tanks dragged around a floor plan until it looks
 * like the room. The floor is a grid of squares, wider than deep; a tank stands on a square of them,
 * bigger the more it holds, and its place is the top left square it covers. How many squares across
 * the floor is, its zoom, is the brewery's (Tyler, 2026-10-05).
 */

export interface Floor {
  cols: number;
  rows: number;
}

/** The floor of a layout that many squares across. */
export const floorOf = (width: number): Floor => ({ cols: width, rows: layoutDepth(width) });

export const FLOOR: Floor = floorOf(USUAL_LAYOUT_WIDTH);

export interface Spot {
  x: number;
  y: number;
}

/** How many squares across a tank is drawn: a 7 bbl tank three, a 15 bbl four, a 60 bbl eight. */
export function sizeOf(capacityLiters: string): number {
  const barrels = Number(fromCanonical(capacityLiters, "bbl").toString());
  return Math.min(8, Math.max(2, Math.round(Math.sqrt(barrels) * 1.1)));
}

/** A spot kept on the floor for a tank of that size. */
export function clampSpot(spot: Spot, size: number, floor: Floor = FLOOR): Spot {
  return {
    x: Math.min(floor.cols - size, Math.max(0, Math.round(spot.x))),
    y: Math.min(floor.rows - size, Math.max(0, Math.round(spot.y))),
  };
}

const covers = (a: Spot, aSize: number, b: Spot, bSize: number) =>
  a.x < b.x + bSize && b.x < a.x + aSize && a.y < b.y + bSize && b.y < a.y + aSize;

/**
 * Where a tank put on the floor without dragging goes: the first spot, row by row from the top left,
 * that no placed tank covers; the top left when the floor is full.
 */
export function freeSpot(
  size: number,
  placed: readonly { spot: Spot; size: number }[],
  floor: Floor = FLOOR,
): Spot {
  for (let y = 0; y + size <= floor.rows; y++) {
    for (let x = 0; x + size <= floor.cols; x++) {
      const spot = { x, y };
      if (!placed.some((p) => covers(spot, size, p.spot, p.size))) return spot;
    }
  }
  return { x: 0, y: 0 };
}

/**
 * The fewest squares across the layout can zoom in to: the smallest width offered that still holds
 * every placed tank where it stands, and is deep enough for the biggest tank in the cellar. Tanks keep
 * their squares from the top left, so zooming in further would leave some off the floor.
 */
export function narrowestWidth(
  placed: readonly { spot: Spot; size: number }[],
  biggest: number,
): number {
  return (
    LAYOUT_WIDTHS.find((w) => {
      const floor = floorOf(w);
      return (
        biggest <= floor.rows &&
        placed.every((p) => p.spot.x + p.size <= floor.cols && p.spot.y + p.size <= floor.rows)
      );
    }) ?? Math.max(...LAYOUT_WIDTHS)
  );
}

/** A tank's capacity, its size, as it was typed: "15 bbl". */
export const capacityLabel = (t: Pick<Tank, "capacity">) =>
  `${t.capacity.value} ${t.capacity.unit}`;

/** A tank's total volume to the top, in its size's unit: "18.5 bbl"; none where it was not given. */
export const totalLabel = (t: Pick<Tank, "capacity" | "totalVolume">) =>
  t.totalVolume === null ? null : `${t.totalVolume} ${t.capacity.unit}`;

/** A capacity shown in another unit, to a tenth: what a batch is held against. */
export const litersIn = (liters: string, unit: "bbl" | "gal" | "hL") =>
  toFixedTrimmed(fromCanonical(liters, unit), 1);

/** The tanks in use, by kind and name as the server lists them; retired ones left out. */
export const inCellar = (tanks: readonly Tank[]) =>
  tanks
    .filter((t) => !t.retired)
    .sort((a, b) => compareTankKinds(a.kind, b.kind) || compareTankNames(a.name, b.name));

/** The kinds the tanks are of, in the order they are listed, each with its tanks. */
export function byKind(tanks: readonly Tank[]): { kind: string; tanks: Tank[] }[] {
  const out: { kind: string; tanks: Tank[] }[] = [];
  for (const t of tanks) {
    const group = out.find((g) => g.kind === t.kind);
    if (group) group.tanks.push(t);
    else out.push({ kind: t.kind, tanks: [t] });
  }
  return out;
}

/** "Fermenters", "Brite tanks", "Yeast brinks", or a brewery's own kind made plural the plain way. */
export function kindPlural(kind: string): string {
  return /s$/i.test(kind) ? kind : `${kind}s`;
}

/** The color a kind is drawn in, as a CSS variable. */
export function kindColor(kind: string): string {
  // Steel, not amber: amber is kept for the beer in a tank (the Amber Is Beer Rule, DESIGN.md).
  if (kind === "Fermenter") return "var(--text-muted)";
  if (kind === "Brite tank") return "var(--info)";
  if (kind === "Yeast brink") return "var(--cat-yeast)";
  return "var(--cat-packaging)";
}

/** What a tank holds today, and the first booking into it that is still to come. */
export function contentsOf(t: Tank): { now: TankStay[]; next: TankStay | null } {
  return {
    now: t.stays.filter((s) => s.state === "in"),
    next: t.stays.find((s) => s.state === "booked") ?? null,
  };
}
